// Shared design tokens for the Bookings module.
//
// TYPE SCALE
// The rest of the app runs on text-[7px] to text-[10px]. A first pass took this
// module to 13px body, which read well on its own but looked oversized and
// shouty sitting next to every other page. This is the settled middle: a couple
// of points above the app's own scale so it stays legible, close enough not to
// look like a different product. Page headings match the text-base h2 used
// everywhere else exactly.
//
// Everything in the module imports from here, so the scale is changed in one
// place rather than across a few thousand class attributes.

export const T = {
  // Section labels, table headers, the small print above a control.
  label: 'text-[9px] font-semibold uppercase tracking-wide',
  // Supporting text: a desk's office, a note, a hint under a button.
  meta: 'text-[10px]',
  // Default body text: table rows, list items, button labels.
  body: 'text-[11px]',
  // Emphasised body: a person's name, a desk label in a card.
  strong: 'text-[12px] font-semibold',
  // Panel and card titles.
  title: 'text-[13px] font-bold',
  // The page heading, matching the text-base h2 every other page uses.
  page: 'text-base font-extrabold',
  // Numbers that are the point of a tile (a count, a percentage).
  figure: 'text-[18px] font-bold tabular-nums',
};

// Whereabouts status colours. Keyed by the `tone` the API returns with each
// status, so an office adding a custom status gets sensible colours without a
// frontend change.
export const TONE = {
  ok:    { bg: 'bg-emerald-50',  border: 'border-emerald-300', text: 'text-emerald-800', dot: 'bg-emerald-500', swatch: '#10b981' },
  info:  { bg: 'bg-blue-50',     border: 'border-blue-300',    text: 'text-blue-800',    dot: 'bg-blue-500',    swatch: '#3b82f6' },
  warn:  { bg: 'bg-amber-50',    border: 'border-amber-300',   text: 'text-amber-800',   dot: 'bg-amber-500',   swatch: '#f59e0b' },
  off:   { bg: 'bg-gray-100',    border: 'border-gray-300',    text: 'text-gray-600',    dot: 'bg-gray-400',    swatch: '#9ca3af' },
  muted: { bg: 'bg-slate-100',   border: 'border-slate-300',   text: 'text-slate-600',   dot: 'bg-slate-400',   swatch: '#94a3b8' },
};

export const tone = (t) => TONE[t] || TONE.muted;

// The desk picker's one accent, in both themes. Indigo carries selection across
// the office cards, the area chips and the floor plan, so the value lives here
// rather than being written out at each use. The light value is duplicated as a
// literal inside FloorPlanMap, which owns the plan's own design language and is
// pinned to it by tests; the dark value is only defined here.
// `dark` is the accent as an outline, a tint or a line of text on a dark surface, where it
// has to be light enough to read against slate. `darkFill` is the same accent as the fill of
// a control with white text on it, where the bright value would only give white about 3:1.
export const PICKER_ACCENT = { light: '#4f46b8', dark: '#8f86f2', darkFill: '#6459d9' };

export const SLOTS = [
  { value: 'full', label: 'All day' },
  { value: 'am',   label: 'Morning' },
  { value: 'pm',   label: 'Afternoon' },
];

export const WEEKDAYS = [
  { iso: 1, short: 'Mon' }, { iso: 2, short: 'Tue' }, { iso: 3, short: 'Wed' },
  { iso: 4, short: 'Thu' }, { iso: 5, short: 'Fri' }, { iso: 6, short: 'Sat' }, { iso: 7, short: 'Sun' },
];

// ── Dates ────────────────────────────────────────────────────────────────────
// All booking dates are plain YYYY-MM-DD. Parsing at midday sidesteps the
// classic off-by-one where a UK date renders as the previous day under BST.

export const isoDate = (d) => {
  const t = new Date(d);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

export const parseDate = (s) => new Date(`${s}T12:00:00`);
export const addDays = (s, n) => { const d = parseDate(s); d.setDate(d.getDate() + n); return isoDate(d); };
export const isoWeekday = (s) => (parseDate(s).getDay() + 6) % 7 + 1;
export const isWeekend = (s) => isoWeekday(s) >= 6;
export const todayIso = () => isoDate(new Date());

// Monday of the week containing `s`.
export const startOfWeek = (s) => addDays(s, -(isoWeekday(s) - 1));

// Sunday of the week containing `s`. The booking window runs to a week end
// rather than to a raw day, so a week is either open or it is not.
export const endOfWeek = (s) => addDays(s, 7 - isoWeekday(s));

export const weekDates = (mondayIso, includeWeekend = false) =>
  Array.from({ length: includeWeekend ? 7 : 5 }, (_, i) => addDays(mondayIso, i));

// Keep a chosen date inside the booking window. The window is per office and
// only known once the offices have loaded, so a repeat's end date is seeded
// from it rather than from a fixed number of days: a hard-coded default opened
// the picker on a date the hint underneath it said was not allowed.
export const clampToWindow = (date, maxDate) => (!date || date > maxDate ? maxDate : date);

export const fmtDay   = (s) => parseDate(s).toLocaleDateString('en-GB', { weekday: 'short' });
export const fmtShort = (s) => parseDate(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
export const fmtLong  = (s) => parseDate(s).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
export const fmtRange = (a, b) => `${fmtShort(a)} to ${fmtShort(b)}`;
